import React from "react";
import { Check } from "lucide-react";
import { Drawer, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle } from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import { useLanguage } from "@/lib/LanguageContext";
import { useParams } from "react-router-dom";
import { cn } from "@/lib/utils";

export default function MobileSelectSheet({ id, open, onOpenChange, title, placeholder, items = [], onSelect, onChange, value, description }) {
  const [internalOpen, setInternalOpen] = React.useState(false);
  const { language } = useLanguage();
  const { lang: routeLang } = useParams();
  const activeLanguage = routeLang || language;
  const isRTL = activeLanguage === "he" || activeLanguage === "ar";
  const controlled = open !== undefined;
  const isOpen = controlled ? open : internalOpen;
  const setOpen = controlled ? onOpenChange : setInternalOpen;
  const selected = items.find((item) => item.value === value);
  const dialogTitle = title || placeholder || "Select an option";

  const handleSelect = (nextValue) => {
    if (onSelect) onSelect(nextValue);
    if (onChange) onChange(nextValue);
    setOpen?.(false);
  };

  return (
    <>
      {!controlled && (
        <Button
          id={id}
          type="button"
          variant="outline"
          className="w-full justify-between rounded-2xl bg-white/90 text-slate-950 shadow-sm dark:bg-white/90 dark:text-slate-950"
          onClick={() => setOpen(true)}
          aria-haspopup="dialog"
          aria-expanded={isOpen}
        >
          <span className="truncate">{selected?.label || placeholder || dialogTitle}</span>
          <span aria-hidden="true">⌄</span>
        </Button>
      )}
      <Drawer open={isOpen} onOpenChange={setOpen}>
        <DrawerContent dir={isRTL ? "rtl" : "ltr"} className="z-[150] rounded-t-[2rem] bg-white/96 text-slate-950 backdrop-blur-2xl dark:bg-white/96 dark:text-slate-950">
          <DrawerHeader className={isRTL ? "text-right" : "text-left"}>
            <DrawerTitle>{dialogTitle}</DrawerTitle>
            <DrawerDescription>{description || "Choose one option from the list."}</DrawerDescription>
          </DrawerHeader>
          <div className="space-y-2 p-4 pb-[calc(1rem+env(safe-area-inset-bottom,0px))]" role="radiogroup" aria-label={dialogTitle}>
            {items.map((item) => {
              const active = value === item.value;
              return (
                <button
                  key={item.value}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => handleSelect(item.value)}
                  className={cn(
                    "flex min-h-14 w-full items-center justify-between gap-3 rounded-2xl px-4 py-3 text-start text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-primary/45",
                    active ? "bg-primary text-primary-foreground" : "bg-slate-100 text-slate-800 hover:bg-slate-200"
                  )}
                >
                  <span className="truncate">{item.label}</span>
                  {active && <Check className="h-5 w-5 shrink-0" aria-hidden="true" />}
                </button>
              );
            })}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}